import React from "react";
import styled from "@emotion/styled";

import { FormattedDate, numberFormatLong } from "components/app_layout/Typography";
import SuppliersContainer from "components/table_components/SuppliersContainer";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import NoticeStage from "lib/core_components/NoticeStage";
import { assertCurrentUser } from "lib/currentUser";
import { Opportunity } from "lib/generated/app-service-gql/graphql";
import { useNoticeListsForRecord } from "lib/hooks/api/notices/lists/useNoticeListsForRecord";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "lib/themes/colors";
import { NoticesStagesv2, OpportunityEntityType } from "lib/types/graphQLEnums";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { useRecordViewer } from "../../../lib/providers/RecordViewer";
import { Flex, Text } from "../../../styles/utility-components";
import { EmptyLogoImage } from "../opportunities_table/OpportunityTableCells";
import { opportunitySavedCardContainer } from "../saved/shared/styles";
import { SaveToButton } from "../saving/SaveToButton";

type BuyerActivity = NonNullable<OpportunityDetails["buyerActivity"]>;
type RelatedNotice = BuyerActivity["relatedNotices"][0];
type RelatedNoticeSuppliers = NonNullable<RelatedNotice["lots"][0]["suppliers"]>;
type SavedNotice = NonNullable<OpportunityDetails["notices"]>[0];

type SaveNoticeButtonProps = {
  notice: {
    id: string;
    name: string;
    value: number | undefined;
    procurementStage: {
      id: string;
      stage: string;
    };
    stage?: string;
    closeDate: string | undefined;
    expiryDate: string | undefined;
    buyers: string[];
    suppliers: string[];
    opportunities?: Pick<Opportunity, "id">[];
    listCount?: number;
  };
};

export function RecommendedNotices({
  relatedNotices: relatedNotices,
  savedNotices,
  isLoading,
}: {
  relatedNotices: RelatedNotice[];
  savedNotices: SavedNotice[];
  isLoading: boolean;
}) {
  const { viewRecord } = useRecordViewer();

  const allNotices: (RelatedNotice | (SavedNotice & { isSaved: true }))[] = [
    ...relatedNotices,
    ...savedNotices.map((notice) => ({ ...notice, isSaved: true })),
    // sort by publish date
  ].sort((a, b) => new Date(b.publishDate ?? 0).getTime() - new Date(a.publishDate ?? 0).getTime());

  if (isLoading) {
    return (
      <Flex column width="100%" gap={32}>
        <SkeletonText width="100%" fontSize={135} variant="rectangular" customSpacing />
        <SkeletonText width="100%" fontSize={135} variant="rectangular" customSpacing />
      </Flex>
    );
  }

  return (
    <Flex column gap={32} width={"100%"}>
      {allNotices.map((notice) => (
        <Container
          key={notice.guid}
          onClick={() => viewRecord(notice.guid, { "Context source": "Opportunity details" })}
        >
          <RecommendedNoticeCard notice={notice} />
        </Container>
      ))}
    </Flex>
  );
}

export function RecommendedNoticeCard({
  notice,
}: {
  notice: RelatedNotice & { isSaved?: boolean };
}) {
  const formattedValue = numberFormatLong(notice.value ?? null);

  const dateInfo = getNoticeDateInfo(notice);

  const { data: noticeLists } = useNoticeListsForRecord(notice.guid);

  return (
    <Flex column gap={16} width="100%">
      <Flex justifyContent="space-between" alignItems="flex-start" gap={24}>
        <Flex gap={8}>
          {notice.stagev2 && <NoticeStage stage={notice.stagev2} />}
          {notice.isSaved && <SavedNoticeTag>Saved notice</SavedNoticeTag>}
        </Flex>
        <SaveNoticeButton
          notice={{
            id: notice.guid,
            name: notice.name ?? "",
            value: notice.value ?? undefined,
            procurementStage: {
              id: notice.procurementStage.id,
              stage: notice.procurementStage.stage ? notice.procurementStage.stage.toString() : "",
            },
            stage: notice.stage || undefined,
            closeDate: notice.closeDate || undefined,
            expiryDate: notice.expiryDate || undefined,
            buyers: notice.buyers.map((buyer) => buyer.guid),
            suppliers: notice.lots.reduce<string[]>(
              (acc, lot) => acc.concat(lot.suppliers?.map((supplier) => supplier.guid) || []),
              [],
            ),
            listCount: noticeLists?.length,
          }}
        />
      </Flex>
      <Text bold color="sysPrimaryDefault">
        {notice.name || "Untitled Notice"}
      </Text>
      <Flex gap={24}>
        <Flex gap={7} alignItems="center">
          <UIcon icon="calendar" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">
            Published <FormattedDate date={notice.publishDate} placeholder="--" />
          </PropertyText>
        </Flex>
        <Flex gap={7} alignItems="center">
          <UIcon icon="hourglass01" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">
            {dateInfo.label} <FormattedDate date={dateInfo.date} placeholder="--" />
          </PropertyText>
        </Flex>
        <Flex gap={7} alignItems="center">
          <UIcon icon="currencyPoundCircle" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">{formattedValue}</PropertyText>
        </Flex>
        <SuppliersPopover notice={notice} />
      </Flex>
    </Flex>
  );
}

function SaveNoticeButton({ notice }: SaveNoticeButtonProps) {
  const currentUser = assertCurrentUser();

  return (
    <SaveToButton
      entityIds={[notice.procurementStage.id]}
      entityType="notice"
      defaultOpportunityValues={{
        name: notice.name,
        value: notice.value,
        assignedToId: currentUser.guid,
        entities: [
          {
            entityId: notice.procurementStage.id,
            entityType: OpportunityEntityType.Notice,
          },
          ...notice.buyers.map((buyer) => ({
            entityId: buyer,
            entityType: OpportunityEntityType.Organisation,
          })),
          ...notice.suppliers.map((id) => ({
            entityId: id,
            entityType: OpportunityEntityType.Organisation,
          })),
        ],
        closeDate: notice.closeDate,
        existingExpiryDate: notice.expiryDate,
        noticeStage: notice.procurementStage.stage,
        stage: notice.stage,
      }}
      opportunities={notice.opportunities}
      savedCount={notice.listCount}
      compactDisplay
      buttonProps={{
        buttonType: "secondary",
        icon: {
          color: sysPrimaryDefault,
          fill: sysPrimaryDefault,
        },
      }}
    />
  );
}

function getNoticeDateInfo(notice: RelatedNotice): { label?: string; date?: string | null } {
  const now = new Date();

  switch (notice.stagev2) {
    case NoticesStagesv2.AwardedContract:
    case NoticesStagesv2.ExpiredContract:
    case NoticesStagesv2.CancelledAward: {
      const date = notice.expiryDate;
      const isFutureDate = date ? new Date(date) > now : false;
      return {
        label: isFutureDate ? "Expires" : "Expired",
        date,
      };
    }

    case NoticesStagesv2.OpenTender:
    case NoticesStagesv2.ClosedTender:
    case NoticesStagesv2.CancelledTender:
    case NoticesStagesv2.UnknownTender: {
      const date = notice.closeDate;
      const isFutureDate = date ? new Date(date) > now : false;
      return {
        label: isFutureDate ? "Closes" : "Closed",
        date,
      };
    }

    default:
      return { label: "", date: "" };
  }
}

function SuppliersPopover({ notice }: { notice: RelatedNotice }) {
  const suppliers = notice.lots.reduce<RelatedNoticeSuppliers>(
    (acc, lot) => acc.concat(lot.suppliers?.map((supplier) => supplier) || []),
    [],
  );

  if (suppliers.length === 0) {
    return null;
  }

  const mappedSuppliers = suppliers.map((s) => ({
    guid: s.guid,
    name: s.name,
  }));

  return (
    <SupplierWrapper alignItems="center" gap={8}>
      <EmptyLogoImage>
        <UIcon icon="trophy01" size={12} />
      </EmptyLogoImage>
      <SuppliersContainer recordName={notice.name ?? "--"} suppliers={mappedSuppliers} ellipsis />
    </SupplierWrapper>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
  padding: 15,
}));

const PropertyText = styled(Text)({
  whiteSpace: "nowrap",
});

const SupplierWrapper = styled(Flex)({
  overflow: "hidden",
});

const SavedNoticeTag = styled(Text)(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundAlternative,
  color: theme.colors.sysTextSecondary,
  padding: "2px 8px",
}));
